import { IconChevronsUpDown, IconChevronDown, IconChevronUp, IconInbox } from './icons';
import {
  getIncidentCategory,
  getIncidentCity,
  getIncidentContractRef,
  getIncidentCreatedAtMs,
  getIncidentEntityName,
  getIncidentPriority,
  getIncidentRideId,
  getIncidentStatus,
  getIncidentSubCategory,
} from './incident';
import { IncidentCategoryChip } from './IncidentCategoryChip';
import { PRIORITY_ORDER, STATUS_ORDER } from './incidentLabels';
import { IncidentPriorityIndicator } from './IncidentPriorityIndicator';
import { IncidentStatusBadge } from './IncidentStatusBadge';
import { formatRelativeDate, formatAbsoluteDateTime } from '../../lib/formatters';
import type { ExtranetIncidentOutput, IncidentPriority, IncidentStatus } from '../../types/extranet-api.types';

type SortKey = 'createdAt' | 'priority' | 'status';
type SortDir = 'asc' | 'desc';

interface IncidentsTableProps {
  items: ExtranetIncidentOutput[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  selectedId: string | null;
  onRowClick: (item: ExtranetIncidentOutput) => void;
  sortBy: SortKey;
  sortDir: SortDir;
  onSortChange: (key: SortKey, dir: SortDir) => void;
}

const SKELETON_ROWS = 10;

export const IncidentsTable = ({
  items,
  total,
  isLoading,
  isFetching,
  selectedId,
  onRowClick,
  sortBy,
  sortDir,
  onSortChange,
}: IncidentsTableProps) => {
  const sorted = sortItems(items, sortBy, sortDir);

  const handleHeaderClick = (key: SortKey) => {
    if (key === sortBy) onSortChange(key, sortDir === 'asc' ? 'desc' : 'asc');
    else onSortChange(key, key === 'createdAt' ? 'desc' : 'asc');
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50/40">
              <Th>N° Ticket</Th>
              <SortableTh active={sortBy === 'createdAt'} dir={sortDir} onClick={() => handleHeaderClick('createdAt')}>
                Date
              </SortableTh>
              <Th>N° Course</Th>
              <Th>Catégorie</Th>
              <Th>Ville</Th>
              <Th>Entité</Th>
              <Th>Contrat</Th>
              <SortableTh active={sortBy === 'status'} dir={sortDir} onClick={() => handleHeaderClick('status')}>
                Statut
              </SortableTh>
              <SortableTh
                active={sortBy === 'priority'}
                dir={sortDir}
                onClick={() => handleHeaderClick('priority')}
                align="right"
              >
                Priorité
              </SortableTh>
            </tr>
          </thead>
          <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} />)
            ) : sorted.length === 0 ? (
              <EmptyRow />
            ) : (
              sorted.map(item => (
                <Row key={item.id} item={item} selected={item.id === selectedId} onClick={() => onRowClick(item)} />
              ))
            )}
          </tbody>
        </table>
      </div>
      {!isLoading && sorted.length > 0 ? (
        <div className="px-4 py-2.5 text-xs text-zinc-500 border-t border-zinc-100 flex items-center justify-between">
          <span>
            <span className="font-mono tabular-nums text-zinc-700">{sorted.length}</span> affiché
            {sorted.length > 1 ? 's' : ''} sur <span className="font-mono tabular-nums text-zinc-700">{total}</span>
          </span>
          <span className="text-zinc-400">Cliquez sur une ligne pour le détail</span>
        </div>
      ) : null}
    </div>
  );
};

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SortableTh = ({
  children,
  active,
  dir,
  onClick,
  align = 'left',
}: {
  children: React.ReactNode;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: 'left' | 'right';
}) => (
  <th className={`px-4 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'}`}>
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide transition-colors',
        align === 'right' ? 'flex-row-reverse' : '',
        active ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-700',
      ].join(' ')}
    >
      <span>{children}</span>
      {active ? (
        dir === 'asc' ? (
          <IconChevronUp width={11} height={11} className="text-zinc-700" />
        ) : (
          <IconChevronDown width={11} height={11} className="text-zinc-700" />
        )
      ) : (
        <IconChevronsUpDown width={11} height={11} className="text-zinc-300" />
      )}
    </button>
  </th>
);

const Row = ({ item, selected, onClick }: { item: ExtranetIncidentOutput; selected: boolean; onClick: () => void }) => {
  const createdAtMs = getIncidentCreatedAtMs(item);
  const entityName = getIncidentEntityName(item);
  return (
    <tr
      onClick={onClick}
      className={[
        'border-b border-zinc-100 last:border-b-0 cursor-pointer transition-colors duration-100',
        selected ? 'bg-zinc-50' : 'hover:bg-zinc-50/60',
      ].join(' ')}
    >
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-900 whitespace-nowrap">{item.id}</td>
      <td
        className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap"
        title={formatAbsoluteDateTime(createdAtMs)}
      >
        {formatRelativeDate(createdAtMs)}
      </td>
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">
        {getIncidentRideId(item)}
      </td>
      <td className="px-4 py-3">
        <IncidentCategoryChip category={getIncidentCategory(item)} subCategory={getIncidentSubCategory(item)} compact />
      </td>
      <td className="px-4 py-3 text-zinc-700">{getIncidentCity(item)}</td>
      <td className="px-4 py-3 text-zinc-700">
        <span className="block max-w-[160px] truncate" title={entityName}>
          {entityName}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">
        {getIncidentContractRef(item)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <IncidentStatusBadge status={getIncidentStatus(item)} />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <IncidentPriorityIndicator priority={getIncidentPriority(item)} />
      </td>
    </tr>
  );
};

const SkeletonRow = () => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3 rounded-sm skeleton-pulse"
          style={{ width: `${[60, 50, 80, 70, 40, 55, 65, 45, 35][i]}%` }}
        />
      </td>
    ))}
  </tr>
);

const EmptyRow = () => (
  <tr>
    <td colSpan={9} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucun incident sur cette période.</p>
          <p className="text-xs text-zinc-500 mt-1">
            Ajustez les filtres ou élargissez la période pour afficher davantage de résultats.
          </p>
        </div>
      </div>
    </td>
  </tr>
);

const sortItems = (items: ExtranetIncidentOutput[], key: SortKey, dir: SortDir): ExtranetIncidentOutput[] => {
  const factor = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    if (key === 'createdAt') {
      return factor * (getIncidentCreatedAtMs(a) - getIncidentCreatedAtMs(b));
    }
    if (key === 'priority') {
      return (
        factor *
        (PRIORITY_ORDER[getIncidentPriority(a) as IncidentPriority] -
          PRIORITY_ORDER[getIncidentPriority(b) as IncidentPriority])
      );
    }
    return (
      factor *
      (STATUS_ORDER[getIncidentStatus(a) as IncidentStatus] - STATUS_ORDER[getIncidentStatus(b) as IncidentStatus])
    );
  });
};
